import React, { useMemo } from 'react'
import { PieChart, Pie, Cell } from 'recharts'
import { useTheme } from '@material-ui/styles'
import Theme from 'core/themes/model'

export interface PieDataEntry {
  value: number
  name: string
  color: string
  strokeColor?: string
}

export interface PieGraphProps {
  data: PieDataEntry[]
  width?: number
  height?: number
  sideLength?: number
  arcWidth?: number
  percent?: number
  startAngle?: number
  endAngle?: number
  primary?: string
  empty?: boolean
  healthColor?: PieDataEntry['color']
  percentagePrecision?: number
}

const PieGraph = ({
  data = [],
  sideLength = 216,
  arcWidth = 56,
  percent = undefined,
  primary,
  empty = false,
  healthColor = 'primary',
  startAngle = 0,
  endAngle = 360,
  width = sideLength + 10,
  height = sideLength + 10,
  percentagePrecision = 0,
  ...rest
}: PieGraphProps) => {
  const theme: Theme = useTheme()
  const radius = Math.floor(sideLength / 2)
  const emptyData = useMemo(
    () => [{ name: 'unknown', value: 1, color: theme.components.graph.tray }],
    [theme.components.graph.tray],
  )
  const items: PieDataEntry[] = empty ? emptyData : data

  return (
    // @ts-ignore
    <PieChart width={width} height={height}>
      <Pie
        dataKey="value"
        data={items}
        startAngle={startAngle}
        endAngle={endAngle}
        cx={radius}
        cy={radius}
        innerRadius={radius - arcWidth}
        outerRadius={radius}
        paddingAngle={0}
        {...rest}
      >
        {items.map((entry) => (
          <Cell
            key={entry.name}
            fill={theme.components.graph[entry.color] || entry.color}
            stroke={entry.strokeColor}
            strokeWidth={entry.value > 0 ? 1 : 0}
          />
        ))}
      </Pie>
      {percent !== undefined && (
        <text
          style={{ ...theme.typography.h4 }}
          x={radius + 5}
          y={radius - 25}
          fill={theme.components.card.text}
          textAnchor="middle"
          dominantBaseline="middle"
        >
          {`${(percent * 100).toFixed(percentagePrecision)}%`}
        </text>
      )}
      {primary && (
        <text
          style={{ ...theme.typography.body2 }}
          x={radius + 5}
          y={radius}
          fill={theme.components.card.text}
          textAnchor="middle"
          dominantBaseline="middle"
        >
          {primary}
        </text>
      )}
    </PieChart>
  )
}

export default PieGraph
